import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import { Button, Modal, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { ReactComponent as CloseSvg } from '@assets/icons/cross.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left-without-line.svg';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as ResizableSplitViewSvg } from '@assets/icons/MediaViewer/resizable-split-view.svg';
import { ReactComponent as SplitViewSvg } from '@assets/icons/MediaViewer/split-view.svg';
import { ReactComponent as SwapSvg } from '@assets/icons/MediaViewer/swap.svg';
import { ReactComponent as RotateSvg } from '@assets/icons/MediaViewer/rotate-v2.svg';
import { ReactComponent as CompareRemoveSvg } from '@assets/icons/MediaViewer/compare-remove.svg';
import type { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';
import { DEFAULT_DRAW_TOOL } from '@pages/MediaViewer/helpers/drawToolShape';
import type { MediaViewerOutletContextState } from '@layouts/types';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { toggleCompareVersionsModal } from '@redux/actions/modalAction';
import { AssetVersionItemDto } from '@api/Api';
import { QUERY_COMMENTS_ASSETS } from '@hooks/queries/asset';
import DropdownVersion from '@pages/MediaViewer/components/CompareVersionsModal/components/DropdownVersion';
import CompareVersionSide from '@pages/MediaViewer/components/CompareVersionsModal/components/CompareVersionSide';
import CompareVersionTools from '@pages/MediaViewer/components/CompareVersionsModal/components/CompareVersionTools';
import UnconfirmedComparison from '@pages/MediaViewer/components/CompareVersionsModal/components/UnconfirmedComparison';
import {
  getLeftVersionDisplayValidityState,
  getRightPanelComparisonState
} from '@pages/MediaViewer/components/CompareVersionsModal/compareVersionComparison';
import {
  alignComparePlayerChromeFromLeft,
  isAudioVideoType,
  syncCompareRightFromLeft
} from '@pages/MediaViewer/components/CompareVersionsModal/comparePositionSync';
import { useComparePlaybackSync } from '@pages/MediaViewer/components/CompareVersionsModal/useComparePlaybackSync';

import './CompareVersionsModal.less';

const COMPARE_SPLIT_STATIC_RATIO = 50;

type CompareVersionsModalProps = {
  isOpen: boolean;
  assetVersions: AssetVersionItemDto[];
  commentMentions: boolean;
};

function CompareVersionsModal({
  isOpen,
  assetVersions,
  commentMentions
}: CompareVersionsModalProps) {
  const { fetchCommentsList } =
    useOutletContext<MediaViewerOutletContextState>();
  const canReadComment = useAssetMVPermissions('readComments');
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const [mode, setMode] = useState<'split' | 'resize-split'>('split');
  const [splitRatio, setSplitRatio] = useState(50);
  const compareBodyRef = useRef<HTMLDivElement>(null);

  const onSplitResizerPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (mode !== 'resize-split') return;
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const body = compareBodyRef.current;
        if (!body) return;
        const rect = body.getBoundingClientRect();
        const w = rect.width;
        if (w <= 0) return;
        const x = ev.clientX - rect.left;
        const raw = (x / w) * 100;
        const pct = Math.min(100, Math.max(0, raw));
        setSplitRatio(Math.round(pct));
      };
      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        handle.releasePointerCapture(e.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
      };
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    },
    [mode]
  );

  // left
  const [leftCommentsOpen, setLeftCommentsOpen] = useState(false);
  const [leftAssetVersion, setLeftAssetVersion] = useState<AssetVersionItemDto>(
    assetVersions?.[0]
  );

  // right
  const [rightCommentsOpen, setRightCommentsOpen] = useState(false);
  const [rightAssetVersion, setRightAssetVersion] =
    useState<AssetVersionItemDto>(assetVersions?.[1]);
  const dispatch = useTypedDispatch();

  const [leftAssetViewerState, setLeftAssetViewerState] =
    useState<IAssetViewerStateContextValue | null>(null);
  const [rightAssetViewerState, setRightAssetViewerState] =
    useState<IAssetViewerStateContextValue | null>(null);
  const [playbackSyncEnabled, setPlaybackSyncEnabled] = useState(false);

  const leftVersionId = leftAssetVersion?.id ?? '';
  const rightVersionId = rightAssetVersion?.id ?? '';
  const canCreateTask = useAssetMVPermissions('convertCommentToTask');

  const { data: leftComments, isLoading: isLoadingLeftCommentsList } = useQuery(
    {
      queryKey: [QUERY_COMMENTS_ASSETS, leftVersionId],
      queryFn: () => fetchCommentsList({ assetVersionId: leftVersionId }),
      enabled: !!leftVersionId && canReadComment
    }
  );
  const { data: rightComments, isLoading: isLoadingRightCommentsList } =
    useQuery({
      queryKey: [QUERY_COMMENTS_ASSETS, rightVersionId],
      queryFn: () => fetchCommentsList({ assetVersionId: rightVersionId }),
      enabled: !!rightVersionId && canReadComment
    });

  const leftCommentsList = leftComments?.comments ?? [];
  const rightCommentsList = rightComments?.comments ?? [];

  const onCloseModal = () => {
    dispatch(toggleCompareVersionsModal(null));
  };

  const onSwapVersions = () => {
    setLeftAssetVersion(rightAssetVersion);
    setRightAssetVersion(leftAssetVersion);
  };

  const onReset = () => {
    leftAssetViewerState?.setDocumentHighlight(null);
    rightAssetViewerState?.setDocumentHighlight(null);
    leftAssetViewerState?.setCanvasMode('view');
    rightAssetViewerState?.setCanvasMode('view');
    leftAssetViewerState?.setDrawToolsShape(DEFAULT_DRAW_TOOL);
    rightAssetViewerState?.setDrawToolsShape(DEFAULT_DRAW_TOOL);
    rightAssetViewerState?.setTurnoverDegree(0);
    leftAssetViewerState?.setTurnoverDegree(0);
    setSplitRatio(50);
  };

  const leftDisplayValidity = useMemo(
    () => getLeftVersionDisplayValidityState(leftAssetVersion),
    [leftAssetVersion]
  );

  const rightPanelComparison = useMemo(
    () => getRightPanelComparisonState(leftAssetVersion, rightAssetVersion),
    [leftAssetVersion, rightAssetVersion]
  );

  const showPlaybackSyncButton = useMemo(
    () =>
      isAudioVideoType(leftAssetVersion?.metadata?.type) &&
      isAudioVideoType(rightAssetVersion?.metadata?.type),
    [leftAssetVersion, rightAssetVersion]
  );

  const canSyncPlayback = useMemo(
    () =>
      showPlaybackSyncButton &&
      !!leftAssetViewerState &&
      !!rightAssetViewerState &&
      !leftDisplayValidity &&
      !rightPanelComparison,
    [
      showPlaybackSyncButton,
      leftAssetViewerState,
      rightAssetViewerState,
      leftDisplayValidity,
      rightPanelComparison
    ]
  );

  const comparePlaybackVersionKey = `${leftVersionId}:${rightVersionId}`;

  useComparePlaybackSync({
    enabled: playbackSyncEnabled,
    left: leftAssetViewerState,
    right: rightAssetViewerState,
    versionKey: comparePlaybackVersionKey
  });

  useEffect(() => {
    if (!isOpen) setPlaybackSyncEnabled(false);
  }, [isOpen]);

  useEffect(() => {
    if (!canSyncPlayback) setPlaybackSyncEnabled(false);
  }, [canSyncPlayback]);

  const onTogglePlaybackSync = useCallback(async () => {
    if (!leftAssetViewerState || !rightAssetViewerState) return;
    if (playbackSyncEnabled) {
      setPlaybackSyncEnabled(false);
      return;
    }
    await syncCompareRightFromLeft(leftAssetViewerState, rightAssetViewerState);
    alignComparePlayerChromeFromLeft(
      leftAssetViewerState,
      rightAssetViewerState
    );
    setPlaybackSyncEnabled(true);
  }, [leftAssetViewerState, rightAssetViewerState, playbackSyncEnabled]);

  const isResizeSplit = mode === 'resize-split';
  const isLeftExpanding =
    isResizeSplit && splitRatio > COMPARE_SPLIT_STATIC_RATIO;
  const isRightExpanding =
    isResizeSplit && splitRatio < COMPARE_SPLIT_STATIC_RATIO;
  const leftPanelWidth = isLeftExpanding
    ? splitRatio
    : COMPARE_SPLIT_STATIC_RATIO;
  const rightPanelWidth = isRightExpanding
    ? 100 - splitRatio
    : COMPARE_SPLIT_STATIC_RATIO;

  if (!asset) return null;

  return (
    <Modal
      className="compare-versions-modal"
      open={isOpen}
      closable={false}
      centered={false}
      width="100%"
      zIndex={100}
      footer={null}
    >
      <div className="compare-versions-modal_head">
        <div className="compare-versions-modal_head-left">
          <Button
            className="compare-versions-modal_btn"
            onClick={onCloseModal}
            icon={<CloseSvg className="b-icon-close" />}
          />
          <DropdownVersion
            selectedVersion={leftAssetVersion}
            onChangeVersion={setLeftAssetVersion}
            versions={assetVersions}
            canReadComment={canReadComment}
            commentCount={leftCommentsList?.length ?? 0}
            toggleOpenComments={() => setLeftCommentsOpen(!leftCommentsOpen)}
          />
        </div>
        <div className="compare-versions-modal_head-actions">
          <Tooltip
            title={
              <div
                className="media-viewer-tooltip-inner"
                style={{ padding: 8 }}
              >
                Swap
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="bottom"
            overlayClassName="toggle_favorite_overlay"
          >
            <Button
              className="compare-versions-modal_btn"
              icon={<SwapSvg />}
              onClick={onSwapVersions}
            />
          </Tooltip>
          <div className="compare-versions-modal_head-actions-separator" />
          <div className="compare-versions-modal_head-actions-block">
            <Tooltip
              title={
                <div
                  className="media-viewer-tooltip-inner"
                  style={{ padding: 8 }}
                >
                  Split Mode
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <Button
                className={classNames('compare-versions-modal_btn', {
                  'compare-versions-modal_btn--active': mode === 'split'
                })}
                icon={<SplitViewSvg />}
                onClick={() => {
                  setMode('split');
                  setSplitRatio(50);
                }}
              />
            </Tooltip>
            <Tooltip
              title={
                <div
                  className="media-viewer-tooltip-inner"
                  style={{ padding: 8 }}
                >
                  Slider Mode
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <Button
                className={classNames('compare-versions-modal_btn', {
                  'compare-versions-modal_btn--active': mode === 'resize-split'
                })}
                icon={<ResizableSplitViewSvg />}
                onClick={() => setMode('resize-split')}
              />
            </Tooltip>
          </div>
          <div className="compare-versions-modal_head-actions-separator" />
          <div className="compare-versions-modal_head-actions-block">
            {showPlaybackSyncButton && (
              <Tooltip
                title={
                  <div
                    className="media-viewer-tooltip-inner"
                    style={{ padding: 8 }}
                  >
                    Sync / Unsync
                  </div>
                }
                overlayInnerStyle={{ padding: 0 }}
                placement="bottom"
                overlayClassName="toggle_favorite_overlay"
              >
                <Button
                  className={classNames('compare-versions-modal_btn', {
                    'compare-versions-modal_btn--active': playbackSyncEnabled
                  })}
                  icon={<CompareRemoveSvg />}
                  disabled={!canSyncPlayback && !playbackSyncEnabled}
                  onClick={onTogglePlaybackSync}
                />
              </Tooltip>
            )}
            <Tooltip
              title={
                <div
                  className="media-viewer-tooltip-inner"
                  style={{ padding: 8 }}
                >
                  Reset all
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="bottom"
              overlayClassName="toggle_favorite_overlay"
            >
              <Button
                className="compare-versions-modal_btn"
                icon={<RotateSvg />}
                onClick={onReset}
              />
            </Tooltip>
          </div>
        </div>
        <div className="compare-versions-modal_head-right">
          <DropdownVersion
            selectedVersion={rightAssetVersion}
            onChangeVersion={setRightAssetVersion}
            canReadComment={canReadComment}
            versions={assetVersions}
            commentCount={rightCommentsList?.length ?? 0}
            toggleOpenComments={() => setRightCommentsOpen(!rightCommentsOpen)}
          />
        </div>
      </div>
      <div
        ref={compareBodyRef}
        className={classNames('compare-versions-modal_body', {
          'compare-versions-modal_body--resize-split': isResizeSplit
        })}
      >
        <CompareVersionTools
          leftAssetViewerState={leftAssetViewerState}
          rightAssetViewerState={rightAssetViewerState}
        />
        <div
          className={classNames('compare-versions-modal_body-block b-left', {
            'compare-versions-modal_body-block--expanding': isLeftExpanding
          })}
          style={{ width: isResizeSplit ? `${leftPanelWidth}%` : '50%' }}
        >
          {!leftDisplayValidity && leftAssetVersion && (
            <CompareVersionSide
              selectedVersion={leftAssetVersion}
              commentsList={leftCommentsList}
              isLoadingCommentsList={isLoadingLeftCommentsList}
              commentsOpen={leftCommentsOpen}
              onCloseCommentsPanel={() => setLeftCommentsOpen(false)}
              setAssetViewerState={setLeftAssetViewerState}
              asset={asset}
              canCreateTask={canCreateTask}
              commentMentions={commentMentions}
            />
          )}
          {!!leftDisplayValidity && (
            <UnconfirmedComparison
              versions={assetVersions}
              asset={asset}
              text={leftDisplayValidity.message}
              showLoader={leftDisplayValidity.showLoader}
              selectedVersion={leftAssetVersion}
              onChangeVersion={setLeftAssetVersion}
            />
          )}
        </div>
        <div
          className={classNames('compare-versions-modal_split-resizer')}
          style={{ left: `${splitRatio}%` }}
          onPointerDown={onSplitResizerPointerDown}
        >
          <div className="compare-versions-modal_split-resizer-line" />
          {isResizeSplit && (
            <div className="compare-versions-modal_split-resizer-btn">
              <ArrowLeftSvg />
              <ArrowRightSvg />
            </div>
          )}
        </div>
        <div
          className={classNames('compare-versions-modal_body-block b-right', {
            'compare-versions-modal_body-block--expanding': isRightExpanding
          })}
          style={{ width: isResizeSplit ? `${rightPanelWidth}%` : '50%' }}
        >
          {!rightPanelComparison && (
            <CompareVersionSide
              selectedVersion={rightAssetVersion}
              commentsList={rightCommentsList}
              isLoadingCommentsList={isLoadingRightCommentsList}
              commentsOpen={rightCommentsOpen}
              onCloseCommentsPanel={() => setRightCommentsOpen(false)}
              setAssetViewerState={setRightAssetViewerState}
              asset={asset}
              canCreateTask={canCreateTask}
              commentMentions={commentMentions}
            />
          )}
          {!!rightPanelComparison && (
            <UnconfirmedComparison
              versions={assetVersions}
              asset={asset}
              text={rightPanelComparison.message}
              showLoader={rightPanelComparison.showLoader}
              selectedVersion={rightAssetVersion}
              onChangeVersion={setRightAssetVersion}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CompareVersionsModal;
